import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  ReceiptReturnInvoice,
  ReceiptReturnInsertData,
  ReceiptReturnVm,
  ReceiptReturnListResponse,
  ReceiptReturnListItem,
  ApiResponse,
} from '@/api/types'
import { appendSortDir, type ListSortDir } from '@/lib/listSort'

/**
 * Receipt Return management service
 * Handles all receipt return-related API operations
 */
export class ReceiptReturnService {
  /**
   * Get paginated list of receipt returns for a branch
   */
  async getAllReceiptReturns(
    branchId: number,
    pageNumber: number,
    pageSize: number,
    sortDir: ListSortDir = 'desc'
  ): Promise<ReceiptReturnListResponse> {
    const url = appendSortDir(endpoints.receiptReturn.getAll(branchId, pageNumber, pageSize), sortDir)
    const response = await apiClient.get<ApiResponse<ReceiptReturnListResponse> | ReceiptReturnListResponse | any>(url)
    const payload = response.data

    // Helper to map receipt returns array
    const mapReceiptReturns = (items: any[]): ReceiptReturnListItem[] => {
      return items.map(item => ({
        id: item.id ?? 0,
        invoiceId: item.invoiceId ?? 0,
        customerId: item.customerId ?? 0,
        recipientAccount: item.recipientAccount ?? 0,
        destinationAccount: item.destinationAccount ?? 0,
        amount: item.amount ?? 0,
        createdDate: item.createdDate ?? null,
        createdBy: item.createdBy ?? null,
        modifiedDate: item.modifiedDate ?? null,
        modifiedBy: item.modifiedBy ?? null,
        branchId: item.branchId ?? 0,
        returnNo: item.returnNo ?? null,
        date: item.date ?? null,
        note: item.note ?? null,
        returnType: item.returnType ?? null,
        invoiceNo: formatInvoiceNo(item.invoiceNo ?? item.InvoiceNo),
        customerName: item.customerName,
      }))
    }

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        const data = payload.data
        if (Array.isArray(data)) {
          return {
            totalRecords: payload.totalRecords ?? data.length,
            pageNumber: payload.pageNumber ?? pageNumber,
            pageSize: payload.pageSize ?? pageSize,
            data: mapReceiptReturns(data),
          }
        }
        return data as ReceiptReturnListResponse
      }

      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: mapReceiptReturns(payload.data),
        }
      }
    }

    if (Array.isArray(payload)) {
      return {
        totalRecords: payload.length,
        pageNumber,
        pageSize,
        data: mapReceiptReturns(payload),
      }
    }

    return {
      totalRecords: payload?.totalRecords ?? 0,
      pageNumber: payload?.pageNumber ?? pageNumber,
      pageSize: payload?.pageSize ?? pageSize,
      data: payload?.data ? mapReceiptReturns(payload.data) : [],
    }
  }

  /**
   * Get invoices by customer ID
   */
  async getInvoicesByCustomerId(customerId: number): Promise<ReceiptReturnInvoice[]> {
    const url = endpoints.receiptReturn.getInvoicesByCustomerId(customerId)
    const response = await apiClient.get<ApiResponse<ReceiptReturnInvoice[]> | ReceiptReturnInvoice[] | any>(url)

    const raw = Array.isArray(response.data)
      ? response.data
      : ('data' in response.data && Array.isArray(response.data.data) ? response.data.data : [])

    return raw.map((inv: any) => ({
      ...inv,
      invoiceId: Number(inv.invoiceId ?? inv.InvoiceId ?? 0),
      invoiceNo: formatInvoiceNo(inv.invoiceNo ?? inv.InvoiceNo ?? inv.invoiceNumber ?? inv.InvoiceNumber),
      customerId: Number(inv.customerId ?? inv.CustomerId ?? 0),
    }))
  }

  /**
   * Get insert data for receipt return
   */
  async getInsertData(branchId: number, invoiceId: number): Promise<ReceiptReturnInsertData> {
    const url = endpoints.receiptReturn.getInsertData(branchId, invoiceId)
    const response = await apiClient.get<ApiResponse<ReceiptReturnInsertData> | ReceiptReturnInsertData | any>(url)
    
    if (response.data && typeof response.data === 'object') {
      if ('data' in response.data) {
        return response.data.data as ReceiptReturnInsertData
      }
      return response.data as ReceiptReturnInsertData
    }
    
    throw new Error('Invalid response format from GetInsertData')
  }

  async getById(id: number): Promise<ReceiptReturnListItem> {
    const url = endpoints.receiptReturn.getById(id)
    const response = await apiClient.get<ApiResponse<any> | any>(url)
    const payload = response.data
    const data = payload && typeof payload === 'object' && 'data' in payload && payload.data
      ? payload.data
      : payload
    return {
      id: data.id ?? data.Id ?? 0,
      invoiceId: data.invoiceId ?? data.InvoiceId ?? 0,
      customerId: data.customerId ?? data.CustomerId ?? 0,
      recipientAccount: data.recipientAccount ?? data.RecipientAccount ?? 0,
      destinationAccount: data.destinationAccount ?? data.DestinationAccount ?? 0,
      amount: data.amount ?? data.Amount ?? 0,
      createdDate: data.createdDate ?? data.CreatedDate ?? null,
      createdBy: data.createdBy ?? data.CreatedBy ?? null,
      modifiedDate: data.modifiedDate ?? data.ModifiedDate ?? null,
      modifiedBy: data.modifiedBy ?? data.ModifiedBy ?? null,
      branchId: data.branchId ?? data.BranchId ?? 0,
      returnNo: data.returnNo ?? data.ReturnNo ?? null,
      date: data.date ?? data.Date ?? null,
      note: data.note ?? data.Note ?? null,
      returnType: data.returnType ?? data.ReturnType ?? null,
      invoiceNo: formatInvoiceNo(data.invoiceNo ?? data.InvoiceNo),
    }
  }

  /**
   * Save receipt return (POST)
   */
  async saveReceiptReturn(data: ReceiptReturnVm): Promise<any> {
    const url = endpoints.receiptReturn.save
    console.log('🔵 [ReceiptReturnService] POST Request URL:', url)
    console.log('🔵 [ReceiptReturnService] POST Request Data:', JSON.stringify(data, null, 2))
    console.log('🔵 [ReceiptReturnService] POST Request Data Object:', data)
    
    try {
      const response = await apiClient.post<ApiResponse<any> | any>(url, data)
      console.log('✅ [ReceiptReturnService] POST Response:', response)
      return response.data
    } catch (error: any) {
      console.error('❌ [ReceiptReturnService] POST Error:', error)
      console.error('❌ [ReceiptReturnService] POST Error Response:', error.response)
      console.error('❌ [ReceiptReturnService] POST Error Data:', error.response?.data)
      throw error
    }
  }

  /**
   * Update receipt return (PUT)
   */
  async updateReceiptReturn(data: ReceiptReturnVm): Promise<any> {
    const url = endpoints.receiptReturn.update
    const response = await apiClient.put<ApiResponse<any> | any>(url, data)
    return response.data
  }
}

export function formatInvoiceNo(value?: string | number | null): string {
  if (value == null) return ''
  const raw = String(value).trim()
  if (!raw) return ''
  if (/^\d+$/.test(raw)) return raw.padStart(6, '0')
  return raw
}

export const receiptReturnService = new ReceiptReturnService()
